<script lang="ts">
  // Interactive overlays for the duel prompts that used to be silently auto-resolved: the chain /
  // response window, card-target selection (min/max + confirm + cancel), tribute selection, and the
  // effect-option menu. EDOPro-style: a non-blocking bar at the bottom, candidate cards shown as art
  // thumbnails (so it works whether they sit on the field, in hand, deck, or limbo), a clear count
  // indicator, and explicit OK / Cancel / No-Response actions. The parent owns the wire; this only
  // collects the player's answer and hands back a DuelChoice.
  import type { DuelChoice } from "@ygoevo/world-protocol";
  import type { CardRef, QueriedCard } from "@ygoevo/protocol";
  import type { Prompt } from "../../stores/duel.svelte.ts";
  import { fly, fade } from "svelte/transition";
  import { cardImage } from "../../../data.ts";

  let {
    prompt,
    nameOf,
    onchoice,
    onhover,
    prochain = null,
    onprochain,
  }: {
    prompt: Prompt;
    nameOf: (code: number) => string;
    onchoice: (c: DuelChoice) => void;
    onhover?: (c: CardRef) => void;
    /** Pro Mode chain window: the NEUTRAL candidate list (your set cards + hand) shown instead of the
     *  prompt's chainable cards, so the window never reveals WHICH cards can chain. null = normal. */
    prochain?: QueriedCard[] | null;
    /** Pro Mode chain pick: the parent validates the clicked candidate against the prompt (legal →
     *  sends the chain answer; illegal → notification, window stays open). */
    onprochain?: (c: QueriedCard) => void;
  } = $props();

  // Local multi-select state for card / tribute prompts. Reset whenever a new prompt arrives.
  let picked = $state<number[]>([]);
  $effect(() => { void prompt; picked = []; });

  const isMulti = $derived(prompt.kind === "selectCard" || prompt.kind === "selectTribute");
  const isIncremental = $derived(prompt.kind === "selectUnselectCard");
  const cards = $derived(isMulti ? (prompt as Extract<Prompt, { cards: CardRef[] }>).cards : []);
  const selectable = $derived(isIncremental ? (prompt as Extract<Prompt, { kind: "selectUnselectCard" }>).selectable : []);
  const alreadyPicked = $derived(isIncremental ? (prompt as Extract<Prompt, { kind: "selectUnselectCard" }>).unselectable : []);
  // SELECT_UNSELECT_CARD expects one combined index: selectable first, then already-selected cards.
  // Keeping that exact wire order makes both adding and removing a choice work from pile pickers.
  const shownCards = $derived(isIncremental ? [...selectable, ...alreadyPicked] : cards);
  const min = $derived(isMulti || isIncremental ? (prompt as { min: number }).min : 0);
  const max = $derived(isMulti || isIncremental ? (prompt as { max: number }).max : 0);
  const cancelable = $derived(isMulti || isIncremental ? (prompt as { cancelable: boolean }).cancelable : false);
  const finishable = $derived(isIncremental && (prompt as Extract<Prompt, { kind: "selectUnselectCard" }>).finishable);
  // Tribute min counts release_param (a monster can count as 2 tributes), not cards — plain
  // selectCard candidates carry no param → 1 each, so weight === picked.length there.
  const weight = $derived(isIncremental
    ? alreadyPicked.length
    : picked.reduce((s, i) => s + ((cards[i] as { releaseParam?: number })?.releaseParam ?? 1), 0));
  const selectedCount = $derived(isIncremental ? alreadyPicked.length : picked.length);
  const enough = $derived(isIncremental ? finishable : weight >= min && picked.length <= max);

  // Human-worded headline / subline for the target banner (vs. terse "Select target(s)").
  const isTribute = $derived(prompt.kind === "selectTribute");
  const remaining = $derived(Math.max(0, min - weight));
  const headline = $derived(
    isIncremental
      ? (max === 1 ? "Choose a card" : (min === max ? `Choose ${min} cards` : `Choose ${min}–${max} cards`))
      : isTribute
        ? (min === max ? `Tribute ${min} monster${min === 1 ? "" : "s"}` : `Tribute ${min}–${max} monsters`)
        : (max === 1 ? "Choose a target" : (min === max ? `Choose ${min} targets` : `Choose ${min}–${max} targets`)),
  );
  const subline = $derived(
    remaining > 0
      ? `Pick ${remaining} more`
      : (selectedCount > max ? "Too many selected" : "Ready, confirm your choice"),
  );

  function toggle(i: number) {
    if (isIncremental) {
      // The core re-prompts after each add/remove. The next prompt carries the authoritative set.
      onchoice({ kind: "unselect", index: i });
      return;
    }
    const at = picked.indexOf(i);
    if (at >= 0) picked = picked.filter((x) => x !== i);
    else if (picked.length < max) picked = [...picked, i];
    else if (max === 1) picked = [i]; // single-pick: replace
  }
  const confirm = () => {
    if (!enough) return;
    onchoice(isIncremental ? { kind: "unselect", index: null } : { kind: "selectCards", indices: picked });
  };
  const label = (c: CardRef) => (c.code ? nameOf(c.code) : "Hidden card");
</script>

<!-- Rendered OUTSIDE the scaled board stage → centred across the full viewport at true size (the
     stage's --rail/--mhand vars don't exist here; use --ui from the sizer for the bottom gap). -->
<div class="pointer-events-none fixed inset-x-0 bottom-0 z-[55] flex justify-center px-3" style="padding-bottom:calc(1.4rem * var(--ui, 1))">
  {#if prompt.kind === "selectChain" || prompt.kind === "selectOption"}
  <div
    class="pointer-events-auto flex max-w-[min(94vw,860px)] flex-col gap-2.5 chamfer bg-void/92 px-4 py-3 ring-1 ring-cyan/35 backdrop-blur-md"
    style="--c:10px;box-shadow:0 10px 40px rgba(0,0,0,.55)"
    in:fly={{ y: 24, duration: 200 }}
    out:fade={{ duration: 120 }}
  >
    {#if prompt.kind === "selectChain"}
      {#if prochain}
        <!-- Pro Mode chain window: a neutral "Respond?" with your SET cards + hand — the normal list
             below auto-reveals WHICH cards can chain, which is exactly the hint Pro Mode withholds.
             The pick is validated by the parent: legal → chains; illegal → notification, window stays. -->
        <div class="flex items-center justify-between gap-3">
          <span class="font-display text-xs font-bold uppercase tracking-[0.16em] text-magenta" style="text-shadow:0 0 12px var(--color-magenta)">Respond?</span>
          <button class="chamfer-btn border border-ink-dim/40 bg-void/60 px-3 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider text-ink-dim transition-colors hover:text-ink" style="--c:4px" onclick={() => onchoice({ kind: "chain", index: -1 })}>No Response</button>
        </div>
        <div class="pro-chain flex max-h-[42dvh] flex-wrap justify-center gap-1.5 overflow-y-auto scroll-cyber">
          {#each prochain as c, i (i)}
            <button
              class="group flex w-[72px] flex-col items-center gap-1 chamfer border border-magenta/30 bg-void/55 p-1 transition-colors hover:bg-magenta/12"
              style="--c:5px"
              title={label(c)}
              onmouseenter={() => onhover?.(c)}
              onclick={() => onprochain?.(c)}
            >
              <img src={cardImage(c.code)} alt={label(c)} class="aspect-[59/86] w-full overflow-hidden chamfer object-cover" style="--c:3px" draggable="false" />
              <span class="w-full truncate text-center font-display text-[0.52rem] uppercase tracking-wide text-ink-dim/80">{label(c)}</span>
            </button>
          {/each}
        </div>
      {:else}
      <!-- chain / response window -->
      <div class="flex items-center justify-between gap-3">
        <span class="font-display text-xs font-bold uppercase tracking-[0.16em] text-magenta" style="text-shadow:0 0 12px var(--color-magenta)">
          {prompt.forced ? "Activate, required" : "Activate in response?"}
        </span>
        {#if !prompt.forced}
          <button class="chamfer-btn border border-ink-dim/40 bg-void/60 px-3 py-1.5 font-display text-[0.62rem] font-bold uppercase tracking-wider text-ink-dim transition-colors hover:text-ink" style="--c:4px" onclick={() => onchoice({ kind: "chain", index: -1 })}>No Response</button>
        {/if}
      </div>
      <div class="flex max-h-[42dvh] flex-col gap-1.5 overflow-y-auto scroll-cyber">
        {#each prompt.chains as c, i (i)}
          <button
            class="group flex items-center gap-2.5 chamfer border border-magenta/40 bg-void/55 p-1.5 text-left transition-colors hover:bg-magenta/12"
            style="--c:5px"
            onmouseenter={() => onhover?.(c)}
            onclick={() => onchoice({ kind: "chain", index: i })}
          >
            <img src={cardImage(c.code)} alt={label(c)} class="aspect-[59/86] w-[42px] shrink-0 overflow-hidden chamfer object-cover" style="--c:3px" draggable="false" />
            <span class="min-w-0 flex-1">
              <span class="block truncate font-display text-[0.66rem] font-bold uppercase tracking-wide text-magenta">{label(c)}</span>
              <span class="block text-[0.62rem] leading-snug text-ink-dim/85" style="display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden">{c.description?.trim() || "Activate this effect"}</span>
            </span>
          </button>
        {/each}
      </div>
      {/if}
    {:else if prompt.kind === "selectOption"}
      <!-- effect option menu (resolved effect text) -->
      <span class="text-center font-display text-xs font-bold uppercase tracking-[0.16em] text-gold" style="text-shadow:0 0 12px var(--color-gold)">Choose an effect</span>
      <div class="flex max-h-[42dvh] flex-col gap-1.5 overflow-y-auto scroll-cyber">
        {#each prompt.options as opt, i (i)}
          <button class="chamfer-btn border border-gold/45 bg-void/60 px-4 py-2 text-left font-display text-[0.7rem] font-semibold normal-case leading-snug text-gold transition-colors hover:bg-gold/15" style="--c:4px" onclick={() => onchoice({ kind: "option", index: i })}>{opt?.trim() || `Effect ${i + 1}`}</button>
        {/each}
      </div>
    {/if}
  </div>
  {:else}
  <!-- card / tribute target selection, prominent, worded target banner -->
  <div
    class="pointer-events-auto flex w-[min(96vw,960px)] flex-col gap-3 chamfer bg-void/95 backdrop-blur-md target-panel"
    style="--c:14px;padding:calc(0.9rem*var(--ui)) calc(1.2rem*var(--ui));--panel-accent:var(--color-gold)"
    in:fly={{ y: 28, duration: 200 }}
    out:fade={{ duration: 120 }}
  >
    <div class="flex items-center justify-between gap-4">
      <div class="flex items-baseline gap-3">
        <span class="font-display font-bold uppercase tracking-[0.14em] text-gold"
              style="font-size:calc(1.05rem*var(--ui));text-shadow:0 0 16px var(--color-gold)">{headline}</span>
        <span class="font-body text-ink-dim/80" style="font-size:calc(0.78rem*var(--ui))">{subline}</span>
      </div>
      <div class="flex items-center gap-3">
        <!-- big count chip -->
        <span class="grid place-items-center chamfer font-display font-extrabold tabular-nums"
              style="--c:6px;width:calc(3.4rem*var(--ui));height:calc(2.1rem*var(--ui));font-size:calc(1rem*var(--ui));
                     color:{enough ? 'var(--color-lime)' : 'var(--color-gold)'};
                     background:color-mix(in oklab,{enough ? 'var(--color-lime)' : 'var(--color-gold)'} 14%,transparent);
                     box-shadow:inset 0 0 0 1.5px color-mix(in oklab,{enough ? 'var(--color-lime)' : 'var(--color-gold)'} 60%,transparent)">
          {selectedCount}/{min === max ? min : `${min}–${max}`}
        </span>
        {#if cancelable}
          <button class="chamfer-btn border border-magenta/50 text-magenta hover:bg-magenta/15 font-display font-bold uppercase tracking-wider"
                  style="--c:5px;padding:calc(0.5rem*var(--ui)) calc(1rem*var(--ui));font-size:calc(0.72rem*var(--ui))"
                  onclick={() => onchoice(isIncremental ? { kind: "unselect", index: null } : { kind: "cancel" })}>Cancel</button>
        {/if}
        <button
          class="chamfer-btn border font-display font-bold uppercase tracking-wider transition-colors {enough ? 'border-lime/70 text-lime hover:bg-lime/15' : 'border-ink-dim/25 text-ink-dim/40'}"
          style="--c:5px;padding:calc(0.5rem*var(--ui)) calc(1.4rem*var(--ui));font-size:calc(0.78rem*var(--ui)){enough ? ';box-shadow:0 0 16px color-mix(in oklab,var(--color-lime) 40%,transparent)' : ''}"
          disabled={!enough}
          onclick={confirm}
        >Confirm</button>
      </div>
    </div>
    <div class="flex flex-wrap justify-center" style="gap:calc(0.6rem*var(--ui))">
      {#each shownCards as c, i (i)}
        {@const sel = isIncremental ? i >= selectable.length : picked.includes(i)}
        {@const order = isIncremental ? i - selectable.length + 1 : picked.indexOf(i) + 1}
        {@const full = !isIncremental && !sel && picked.length >= max && max > 1}
        <button
          class="group relative shrink-0 flex flex-col items-center transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
          style="width:calc(5.2rem*var(--ui))"
          disabled={full}
          title={label(c)}
          onmouseenter={() => onhover?.(c)}
          onclick={() => toggle(i)}
        >
          <span class="relative w-full overflow-hidden chamfer ring-2 transition-all {sel ? 'ring-lime' : 'ring-cyan/45 group-hover:ring-cyan'}"
                style="--c:6px;{sel ? 'box-shadow:0 0 18px color-mix(in oklab,var(--color-lime) 60%,transparent)' : ''}">
            <img src={cardImage(c.code)} alt={label(c)} class="aspect-[59/86] w-full object-cover {sel ? '' : 'opacity-90'}" draggable="false" />
            {#if sel}
              <span class="absolute right-1 top-1 grid place-items-center rounded-full bg-lime font-display font-bold text-void"
                    style="width:calc(1.3rem*var(--ui));height:calc(1.3rem*var(--ui));font-size:calc(0.7rem*var(--ui))">{order}</span>
            {/if}
          </span>
          <span class="mt-1 w-full truncate text-center font-display uppercase tracking-wide {sel ? 'text-lime' : 'text-ink-dim/70'}"
                style="font-size:calc(0.55rem*var(--ui))">{label(c)}</span>
        </button>
      {/each}
    </div>
  </div>
  {/if}
</div>

<style>
  /* Prominent target banner: accent ring + glow + arrive flash so a target query reads as the
     loudest non-card element on screen. --panel-accent is set inline (gold by default). */
  .target-panel {
    box-shadow:
      0 0 0 1.5px color-mix(in oklab, var(--panel-accent) 65%, transparent),
      0 0 26px color-mix(in oklab, var(--panel-accent) 28%, transparent),
      0 14px 48px rgba(0, 0, 0, 0.6);
    animation: panel-arrive 0.22s ease-out;
  }
  @keyframes panel-arrive {
    from {
      box-shadow: 0 0 0 3px var(--panel-accent);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .target-panel {
      animation: none;
    }
  }
</style>
